import type { CustomKeyboardLayout, MenuButton } from '@bale-bot/types';
import { BOT_ACTIONS } from '@bale-bot/config';

/**
 * Builds an action → label map from a menu layout.
 * One walk of the tree; subsequent lookups are O(1).
 *
 * Covers:
 *  - leaf callback_data
 *  - folder actions (submenu_<id>)
 *  - legacy flat submenus
 */
export function buildActionLabelMap(
  layout: CustomKeyboardLayout | undefined | null,
): Map<string, string> {
  const map = new Map<string, string>();
  if (!layout) return map;

  const visit = (rows: MenuButton[][] | MenuButton[]) => {
    const normalized: MenuButton[][] = Array.isArray(rows[0])
      ? (rows as MenuButton[][])
      : (rows as MenuButton[]).map((b) => [b]);

    for (const row of normalized) {
      for (const btn of row) {
        if (btn.callback_data) {
          map.set(btn.callback_data, btn.text);
        }
        if (btn.id && btn.submenu) {
          // Folder action as emitted by keyboards.ts
          map.set(`${BOT_ACTIONS.SUBMENU}_${btn.id}`, btn.text);
        }
        if (btn.submenu) {
          visit(btn.submenu as MenuButton[][] | MenuButton[]);
        }
      }
    }
  };

  visit(layout);
  return map;
}

/** O(1) after the map is built. Falls back to the raw action. */
export function actionToLabel(
  action: string,
  labels: Map<string, string>,
): string {
  return labels.get(action) ?? action;
}